import { useEffect, useRef, useState } from "react";
import { useGlobalState } from "../contexts/GlobalStateContext";
import MessageModal from "./MessageModal";
import { useWebSocketListener } from "./WebSocketService";
import { fetchUsername } from "../Helpers/utils";
import { useNavigate } from "react-router-dom";
import Post from "./Post";
import { createPortal } from "react-dom";
import RepliesModal from "./RepliesModal";

export default function Notifications({ onClose }) {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const {
    globalState,
    updateNotificationCount,
    logOut,
    resetGlobalState,
    setConversations,
    setSelectedConvo,
    setMessageJD,
    setFullScreenPost
  } = useGlobalState();
  const [activeSection, setActiveSection] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [hasUnread, setHasUnread] = useState({
    like: false,
    comment: false,
    follow: false,
  });
  const [isSectionOpen, setIsSectionOpen] = useState(false);
  const offsetMapRef = useRef({ like: 0, comment: 0, follow: 0 });
  const limit = 12;
  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const notificationsContainerRef = useRef(null);
  const userId = globalState.user?.user_id;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [chosenPost, setChosenPost] = useState(null);
  const [parentCommentID, setParentCommentID] = useState(null);
 

  useEffect(() => {
    if (!globalState.isAuthenticated) return;
    fetchConversations();
    fetchNotificationStatus();
  }, []);

  async function fetchFullName(userId) {
    try {
      const res = await fetch("/php/get_fullName.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await res.json();
      if (data.success) {
        return data.fullName;
      } else {
        console.error("Failed to get full name:", data.message);
        return null;
      }
    } catch (err) {
      console.error("Error fetching full name:", err);
      return null;
    }
  }

  useWebSocketListener((data) => {
    if (!globalState.isAuthenticated || !data?.type) return;

    switch (data.type) {
      case "new_message":
        (async () => {
          let fullName = data.fullName;

          if (!fullName) {
            fullName = await fetchFullName(data.sender_id); // must be defined/imported
          }

          setConversations((prevConversations) => {
            const filtered = prevConversations.filter(
              (c) => c.conversation_id !== data.conversation_id
            );

            return [
              {
                conversation_id: data.conversation_id,
                user_id: data.sender_id,
                fullName,
                last_message: data.content,
                has_unread: true,
              },
              ...filtered,
            ];
          });
        })();
        break;

      case "new_like":
      case "new_comment":
      case "new_follow":
        setHasUnread((prev) => ({
          ...prev,
          [data.type.replace("new_", "")]: true,
        }));
        break;

      default:
        console.warn("Unhandled WebSocket message type:", data.type);
    }
  });

  const fetchConversations = async (loadMore = false) => {
    if (isLoading || !userId) return;
    setIsLoading(true);

    try {
      const res = await fetch(
        `${baseUrl}/php/fetch_conversations.php?offset=${offset}&limit=${limit}}`
      );
      const data = await res.json();

      if (data.success) {
        setConversations((prev) =>
          loadMore ? [...prev, ...data.conversations] : data.conversations
        );
        if (data.conversations.length < limit) setHasMore(false);
        else setOffset((prev) => prev + limit);
      } else if (data.status === "error") {
        logOut();
        resetGlobalState();
      } else {
        console.error("Failed to fetch conversations:", data.message);
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickConversation = (convo) => {
    setSelectedConvo(convo);

    // Mark this conversation as read in state
    setConversations((prev) =>
      prev.map((c) =>
        c.conversation_id === convo.conversation_id
          ? { ...c, has_unread: false }
          : c
      )
    );
  };

  useEffect(() => {
    return () => {
      if (globalState.selectedConvo?.conversation_id) {
        // Update local state on unmount
        setConversations((prev) =>
          prev.map((c) =>
            c.conversation_id === globalState.selectedConvo.conversation_id
              ? { ...c, has_unread: false }
              : c
          )
        );

        // Optional: Mark as read on server
        fetch("/php/read_by_receiver.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            conversation_id: globalState.selectedConvo.conversation_id,
          }),
        }).catch((err) => console.error("Failed to mark convo as read:", err));
      }
    };
  }, [globalState.selectedConvo]);

  const handleCloseModal = () => {
    setSelectedConvo(null);
  };

  function closeNotifications() {
    if (isSectionOpen) {
      setIsSectionOpen(false);
      setNotifications([]);
      setActiveSection(null);
    } else {
      onClose?.(); // Safe call
    }
  }

  async function fetchNotificationStatus() {
    try {
      const res = await fetch("/php/fetch_notifications_status.php");
      const data = await res.json();
      if (data.success) {
        const unread = { like: false, comment: false, follow: false };
        data.notifications.forEach((n) => {
          if (!n.is_read) {
            if (n.type === "reply" || n.type === "comment") {
              unread.comment = true; // treat reply as comment
            } else if (n.type === "like") {
              unread.like = true;
            } else if (n.type === "follow") {
              unread.follow = true;
            }
          }
        });
        setHasUnread(unread);
      } else if (data.status === "error") {
        logOut();
        resetGlobalState();
      }
    } catch (e) {
      console.error("Failed to fetch notification status", e);
    }
  }

  async function fetchNotifications(type, loadMore = false) {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      const offset = offsetMapRef.current[type] || 0;
      const res = await fetch(
        `${baseUrl}/php/fetch_notifications.php?type=${type}&offset=${offset}&limit=${limit}`
      );
      const data = await res.json();

      if (data.success) {
        const newNotifs = data.notifications.map((n) => ({
          ...n,
          section: type,
        }));
        setNotifications((prev) =>
          loadMore ? [...prev, ...newNotifs] : newNotifs
        );
        hasMoreRef.current = data.notifications.length >= limit;
        offsetMapRef.current[type] += limit;
      } else if (data.status === "error") {
        logOut();
        resetGlobalState();
      }
    } catch (e) {
      console.error("Error fetching notifications:", e);
    } finally {
      isFetchingRef.current = false;
    }
  }

  function clearAndShowSection(type) {
    setActiveSection(type);
    setIsSectionOpen(true);
    setNotifications([]);
    offsetMapRef.current[type] = 0;
    fetchNotifications(type);
    markCategoryAsRead(type);
  }

  async function markCategoryAsRead(type) {
    try {
      const res = await fetch("/php/mark_category_as_read.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type }),
      });

      if (!res.ok) {
        console.error("Failed to mark category as read");
        return;
      }

      const data = await res.json();

      if (data.success) {
        fetchNotificationStatus();
        updateNotificationCount();
      } else if (data.status === "error") {
        logOut();
        resetGlobalState();
      } else {
        console.warn("Server returned an error:", data.message);
      }
    } catch (error) {
      console.error("Error in markCategoryAsRead:", error);
    }
  }

  // Modify markAsRead to return success flag
  async function markAsRead(notification_id) {
    try {
      const response = await fetch("php/mark_notification_as_read.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ notification_id }),
      });
      const data = await response.json();
      if (data.success) {
        return true;
      } else {
        console.error("Failed to mark notification as read:", data.message);
        return false;
      }
    } catch (error) {
      console.error("Error:", error);
      return false;
    }
  }

  async function redirectToUserProfile(userId) {
    const username = await fetchUsername(userId);
    navigate(`/@${username}`);
    onClose();
  }
  const [embeddedPost, setEmbeddedPost] = useState(null);
  useEffect(() => {
    if (chosenPost) {
      const fetchPost = async () => {
        try {
          const response = await fetch(`${baseUrl}/php/fetch_post.php`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ uuid: chosenPost }),
          });

          const data = await response.json();

          if (data.success) {
            setEmbeddedPost(<Post
              key={data.post.uuid}
              post={data.post}
              onImageClick={() => {
                setFullScreenPost({
                  ...data.post,
                  isExternalClick: true
                }); setEmbeddedPost(null); setChosenPost(null);
              }}
            />) ;
          } else {
            setEmbeddedPost(
              <div
                style={{
                  textAlign: "center",
                  paddingTop: "70px",
                  paddingBottom: "100px",
                  color: "#6B7280",
                }}
              >
                {data.message || "Post unavailable."}
              </div>
            );
          }
        } catch (error) {
          console.error("Error fetching post:", error);
          setEmbeddedPost(
            <div className="text-sm text-center text-red-500 p-2">
              Error fetching post.
            </div>
          );
        }
      };

      fetchPost();
    }
  }, [chosenPost]);

  return (
    <>
      {parentCommentID && (
        <RepliesModal
          uuid={chosenPost}
          parentCommentID={parentCommentID}
          onClose={() => {
            setParentCommentID(null);
          }}
        />
      )}

      {embeddedPost &&
        createPortal(
          <div className="post_modal" style={{ display: "block" }}>
            <div
              className="post_container"
              style={{ padding: "15px 2px 2px 2px" }}
            >
              <span
                className="close"
                onClick={() => {
                  setEmbeddedPost(null);
                  setChosenPost(null);
                  setMessageJD(null);
                }}
                role="button"
                tabIndex={0}
                aria-label="Close modal"
              >
                &times;
              </span>
              <span
                style={{
                  display: "block",
                  textAlign: "center",
                  marginBottom: "15px",
                  color: "#6B7280",
                }}
              >
                {globalState.messageJD && globalState.messageJD}
              </span>
              {embeddedPost}
            </div>
          </div>,
          document.body
        )}
      {globalState.selectedConvo && (
        <MessageModal
          convo={globalState.selectedConvo}
          setConversations={setConversations}
          onClose={handleCloseModal}
        />
      )}
      <div id="notificationSideNav" className="notification_nav">
        <span className="notification_label">Notifications</span>
        <div
          className="closebtn"
          id="closeOrGoBack"
          onClick={closeNotifications}
        >
          {isSectionOpen ? "⥷" : "×"}
        </div>

        {!isSectionOpen && (
          <>
            <div
              id="notificationsContainer"
              ref={notificationsContainerRef}
              style={{
                display: "flex",
                width: "100%",
              }}
            >
              {["like", "comment", "follow"].map((type) => (
                <div
                  key={type}
                  className={`notification ${
                    hasUnread[type] ? "has-unread" : "transparent-bg"
                  }`}
                  onClick={() => clearAndShowSection(type)}
                  style={{
                    flex: 1, // makes them take equal space
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    padding: "5px 10px",
                  }}
                >
                  <img
                    src={`icon/${type}s-icon.png`}
                    alt={`${type} Icon`}
                    className="notification-icon"
                    style={{
                      width: "50px",
                      height: "50px",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    className="notification-content"
                    style={{ marginTop: "8px", textAlign: "center" }}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}s
                  </div>
                </div>
              ))}
            </div>
            <h3 style={{ margin: "5px auto 0 auto" }}>Messages</h3>
            <div id="conversations-section">
              {globalState.conversations.map((convo) => (
                <div
                  key={convo.conversation_id}
                  className={`conversation ${
                    convo.has_unread
                      ? "unread-conversation"
                      : "read-conversation"
                  }`}
                  onClick={() => handleClickConversation(convo)}
                >
                  <img
                    src={`profile_pics_icons/${convo.user_id}.jpg`}
                    alt="Profile"
                    className="profile-pic"
                    onClick={() => redirectToUserProfile(convo.user_id)}
                  />
                  <div className="conversation-content">
                    <strong>{convo.fullName}</strong>
                    <br />
                    <span>
                      {convo.last_message
                        ? convo.last_message
                        : convo.sender_id === globalState.user.user_id
                        ? "You Shared a Post"
                        : "Shared a Post"}
                    </span>
                  </div>
                </div>
              ))}
              {isLoading && (
                <p style={{ margin: "5px auto", textAlign: "center" }}>
                  Loading...
                </p>
              )}

              {!hasMore && globalState.conversations.length > 0 && (
                <p style={{ marginBottom: "60px", textAlign: "center" }}>
                  No more conversations.
                </p>
              )}
            </div>
          </>
        )}

        {isSectionOpen && (
          <div id="notifications-section">
            {notifications.map((n, i) => (
              <div
                key={n.notification_id || i}
                className="notification"
                onClick={async () => {
                  const success = await markAsRead(n.notification_id);
                  if (success) {
                    // Update local state to mark as read immediately
                    setNotifications((prev) =>
                      prev.map((notif) =>
                        notif.notification_id === n.notification_id
                          ? { ...notif, read_in_details: true }
                          : notif
                      )
                    );
                  }
                  if (n.type === "follow") {
                    redirectToUserProfile(n.actor_id);
                  } else if (n.type === "reply") {
                    setChosenPost(n.target_id);
                    setParentCommentID(n.parent_comment_id);
                  } else {
                    setChosenPost(n.target_id);
                  }
                }}
                style={{
                  backgroundColor: n.read_in_details ? "transparent" : "gray",
                }}
              >
                <img
                  src={`profile_pics_icons/${n.actor_id}.jpg`}
                  alt="Profile"
                  className="profile-pic"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent parent onClick from firing
                    redirectToUserProfile(n.actor_id);
                  }}
                />
                <div className="notification-content">{n.message}</div>
              </div>
            ))}

            {hasMoreRef.current && (
              <div
                className="notification"
                onClick={() => fetchNotifications(activeSection, true)}
              >
                <div className="notification-content">
                  Load More {activeSection}s
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
